import { FC, ReactNode } from 'react';

import { Loading } from '@shared/components/Loading';
import { CsMemoStats, StatBucket } from '@shared/utils/csMemoStats';

//* 표를 줄글로 훑는 대신 "어디에 몰리나"를 먼저 보여주는 패널. 답례품·화환 로그 공용.
//* 색은 정보를 나르지 않는다 — 막대는 전부 한 색(크기만 의미), 구분은 라벨·숫자가 한다.
//* 그래야 색맹·흑백 인쇄에서도 같은 결론이 나오고, 유형이 늘어도 팔레트가 안 꼬인다.
//* 값이 없는 축(화환의 발송매체 등)은 카드째로 안 뜬다.

//* 화면 계열색만 갈린다(답례품 indigo · 화환 cyan) — 막대는 여전히 화면당 한 색
const ACCENT = {
  indigo: { bar: 'bg-indigo-500', hover: 'hover:bg-indigo-50', note: 'border-indigo-100 bg-indigo-50/60 text-indigo-900' },
  cyan: { bar: 'bg-cyan-600', hover: 'hover:bg-cyan-50', note: 'border-cyan-100 bg-cyan-50/60 text-cyan-900' },
};

export type CsMemoStatsAccent = keyof typeof ACCENT;

const TRACK = 'bg-gray-100';

/** 순위 막대 — 라벨 / 막대 / 숫자. query가 있으면 눌러서 그 건들만 다시 조회한다 */
const RankBars: FC<{
  rows: StatBucket[];
  accent: CsMemoStatsAccent;
  onPick?: (query: string) => void;
  emptyText?: string;
}> = ({ rows, accent, onPick, emptyText = '데이터 없음' }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-300">{emptyText}</p>;
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((row) => {
        const clickable = !!(row.query && onPick);
        const content = (
          <>
            <span className="w-24 shrink-0 truncate text-gray-600" title={row.label}>
              {row.label}
            </span>
            <span className={`h-2 flex-1 rounded-sm ${TRACK}`}>
              <span
                className={`block h-2 rounded-sm ${ACCENT[accent].bar}`}
                style={{ width: `${Math.max(2, (row.count / max) * 100)}%` }}
              />
            </span>
            <span className="w-9 shrink-0 text-right font-semibold tabular-nums text-gray-700">
              {row.count}
            </span>
          </>
        );
        return (
          <li key={row.label}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onPick?.(row.query ?? '')}
                title={`"${row.query ?? ''}" 로 검색`}
                className={`flex w-full items-center gap-2 rounded px-1 py-0.5 text-xs ${ACCENT[accent].hover}`}
              >
                {content}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-1 py-0.5 text-xs">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/** 시간축 막대 — 일자별·시간대별처럼 순서가 의미 있는 축 (정렬 금지) */
const TimeBars: FC<{
  rows: StatBucket[];
  accent: CsMemoStatsAccent;
  //* 라벨을 전부 찍으면 뭉개진다 — n번째마다 하나씩만
  labelEvery: number;
  suffix?: string;
}> = ({ rows, accent, labelEvery, suffix = '' }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div>
      <div className="flex h-24 items-end gap-[2px]">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex-1"
            title={`${row.label}${suffix} · ${row.count}건`}
          >
            <div
              className={`w-full rounded-t-[3px] ${
                row.count > 0 ? ACCENT[accent].bar : TRACK
              }`}
              style={{ height: `${row.count > 0 ? Math.max(4, (row.count / max) * 96) : 2}px` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-[2px] text-[10px] text-gray-400">
        {rows.map((row, i) => (
          <div key={row.label} className="flex-1 truncate text-center">
            {i % labelEvery === 0 ? row.label.slice(-5) : ''}
          </div>
        ))}
      </div>
    </div>
  );
};

const Card: FC<{ title: string; hint?: string; children: ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <div className="rounded-lg border bg-white p-3">
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <h3 className="text-xs font-semibold text-gray-700">{title}</h3>
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </div>
    {children}
  </div>
);

const Tile: FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="rounded-lg border bg-white px-3 py-2">
    <div className="text-[11px] text-gray-500">{label}</div>
    <div className="text-lg font-bold leading-tight text-gray-800">{value}</div>
    {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
  </div>
);

const pct = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '0%';

export interface CsMemoLogStatsPanelProps {
  stats: CsMemoStats | null;
  isLoading: boolean;
  /** true = 2000건 상한에 걸려 일부만 집계 */
  capped: boolean;
  /** 막대를 눌렀을 때 그 검색어로 목록을 다시 조회 */
  onPickQuery: (query: string) => void;
  accent?: CsMemoStatsAccent;
  /** 도메인마다 뜻이 달라 이름을 받는다 (답례품=교환·배송 통합 / 화환=배송) */
  statusTitle?: string;
}

export const CsMemoLogStatsPanel: FC<CsMemoLogStatsPanelProps> = function Panel({
  stats,
  isLoading,
  capped,
  onPickQuery,
  accent = 'indigo',
  statusTitle = '상태',
}) {
  if (isLoading) {
    return (
      <div className="center-box rounded-lg border border-dashed bg-white py-12">
        <Loading />
      </div>
    );
  }
  if (!stats || stats.counted === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center text-sm text-gray-400">
        집계할 메모가 없습니다.
      </div>
    );
  }

  const topInquiry = stats.inquiryTypes[0];
  const repeatRate = pct(stats.repeatAxes, stats.totalAxes);

  return (
    <div className="space-y-3">
      {/* 한 줄 요약 — 숫자를 읽는 대신 결론부터 준다 (매뉴얼 목차의 출발점) */}
      <div
        className={`rounded-lg border px-3 py-2 text-xs leading-relaxed ${ACCENT[accent].note}`}
      >
        {stats.counted}건 중{' '}
        {topInquiry ? (
          <>
            가장 많은 문의는 <b>{topInquiry.label}</b>({topInquiry.count}건,{' '}
            {pct(topInquiry.count, stats.counted)}) ·{' '}
          </>
        ) : null}
        같은 건에 두 번 이상 응대한 비율 <b>{repeatRate}</b>
        {stats.peakHour ? (
          <>
            {' '}
            · 문의가 가장 몰리는 시간 <b>{stats.peakHour.label}시</b>
          </>
        ) : null}
        {stats.peakDay ? (
          <>
            {' '}
            · 최다일 <b>{stats.peakDay.label.slice(5)}</b>({stats.peakDay.count}건)
          </>
        ) : null}
        {capped && (
          <span className="ml-1 text-amber-700">
            ※ 최근 2,000건까지만 집계했습니다 — 기간을 나눠 보세요.
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Tile
          label="집계 건수"
          value={`${stats.counted}건`}
          sub={capped ? `전체 ${stats.total}건 중` : '조회 조건 전체'}
        />
        <Tile
          label="하루 평균"
          value={`${stats.perDay.toFixed(1)}건`}
          sub={`응대한 날 ${stats.days}일`}
        />
        <Tile
          label="재응대"
          value={repeatRate}
          sub={`${stats.repeatAxes} / ${stats.totalAxes}건`}
        />
        <Tile
          label="관리자 전용"
          value={pct(stats.adminOnly, stats.counted)}
          sub={`${stats.adminOnly}건`}
        />
      </div>

      <Card title="일자별 응대" hint={`${stats.days}일 · 최대 막대에 맞춘 상대 높이`}>
        <TimeBars
          rows={stats.byDay}
          accent={accent}
          labelEvery={Math.max(1, Math.ceil(stats.byDay.length / 8))}
        />
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <Card title="문의 유형" hint="눌러서 검색 · 중복 집계">
          <RankBars
            rows={stats.inquiryTypes}
            accent={accent}
            onPick={onPickQuery}
            emptyText="사전에 걸린 유형 없음"
          />
        </Card>

        <Card title="자주 나온 말" hint="눌러서 검색">
          <RankBars rows={stats.keywords} accent={accent} onPick={onPickQuery} />
        </Card>

        <Card title="시간대" hint="0~23시 · 요일">
          <TimeBars rows={stats.byHour} accent={accent} labelEvery={4} suffix="시" />
          <div className="mt-2">
            <RankBars rows={stats.byWeekday} accent={accent} />
          </div>
        </Card>

        {/* 값이 없는 축은 카드째로 숨긴다 — 화환엔 단위·매체가 없고, 답례품엔 결제상태가 없다 */}
        {(stats.byUnit.length > 0 || stats.byOrderType.length > 0) && (
          <Card title="단위 · 주문 유형">
            <RankBars rows={stats.byUnit} accent={accent} />
            {stats.byOrderType.length > 0 && (
              <div className="mt-2 border-t border-dashed pt-2">
                <RankBars rows={stats.byOrderType} accent={accent} />
              </div>
            )}
          </Card>
        )}

        {stats.byPaymentStatus.length > 0 && (
          <Card title="결제 상태" hint="응대 시점이 아니라 현재 상태">
            <RankBars rows={stats.byPaymentStatus} accent={accent} />
          </Card>
        )}

        {stats.byStatus.length > 0 && (
          <Card title={statusTitle} hint="응대 시점이 아니라 현재 상태">
            <RankBars rows={stats.byStatus} accent={accent} />
          </Card>
        )}

        {stats.byBrand.length > 0 && (
          <Card title="상조" hint="상위 8곳">
            <RankBars rows={stats.byBrand} accent={accent} />
          </Card>
        )}

        {stats.byItem.length > 0 && (
          <Card title="문의 많은 상품" hint="상위 6개">
            <RankBars rows={stats.byItem} accent={accent} />
          </Card>
        )}

        {stats.byChannel.length > 0 && (
          <Card title="발송 매체" hint="쿠폰 건만">
            <RankBars rows={stats.byChannel} accent={accent} />
          </Card>
        )}

        <Card title="작성자">
          <RankBars rows={stats.byWriter} accent={accent} />
        </Card>
      </div>
    </div>
  );
};
